// scid-resolver: resolve serviceConfigId → { sala, code } a partir de
// `serviceConfigs/previsions` (monolito), já que a sala NÃO vem no JourneyTicketDto
// (decisão de fronteira — ver docs/USO_ENDPOINTS_LEGADO_JORNADAS.md §4-5).
// Cache em memória, populado no boot do RealDataSource e invalidável por PrevisionChanged.
import { http, apiV1, lid } from '../http/client'
import type { ServiceConfigPrevisionDto } from '../dto/reference.dto'
import type { QueueWait } from '../../domain/types'
import { env } from '../../config/env'

export interface ScidInfo { sala: string; code: string; nome: string; ticketsWaiting?: number }
export type ValidServiceConfigPrevisionDto = ServiceConfigPrevisionDto & { serviceConfigId: number }

let _cache: Map<number, ScidInfo> | null = null

export function previsionServiceConfigId(p: ServiceConfigPrevisionDto): number | null {
  const id = p.serviceConfigId ?? p.ServiceConfigId ?? p.serviceConfig?.id ?? p.serviceConfig?.Id ?? p.ServiceConfig?.id ?? p.ServiceConfig?.Id ?? p.id ?? p.Id
  return Number.isFinite(id) ? Number(id) : null
}

export function queueWaitsFromPrevisions(previsions: ServiceConfigPrevisionDto[]): QueueWait[] {
  return previsions.flatMap((prevision) => {
    const serviceConfigId = previsionServiceConfigId(prevision)
    if (serviceConfigId == null) {return []}
    const waiting = prevision.ticketsWaiting ?? prevision.TicketsWaiting
    if (typeof waiting !== 'object' || waiting == null) {return []}
    const total = Number(waiting.total ?? waiting.Total ?? waiting.count ?? waiting.waiting)
    return [{
      serviceConfigId,
      total: Number.isFinite(total) && total > 0 ? total : 0,
      nextPrevision: waiting.nextPrevision ?? waiting.NextPrevision ?? null,
    }]
  })
}

export async function loadQueueWaits(): Promise<QueueWait[]> {
  const previsions = await http.get<ServiceConfigPrevisionDto[]>('v1', apiV1(`locations/${lid()}/serviceConfigs/previsions`), { justToday: true })
  return queueWaitsFromPrevisions(previsions)
}

export async function loadPrevisions(): Promise<ServiceConfigPrevisionDto[]> {
  const list = await http.get<ServiceConfigPrevisionDto[]>('v1', apiV1(`locations/${lid()}/serviceConfigs/previsions`), { justToday: env.singleDay })
  _cache = new Map(list.flatMap((p) => {
    const serviceConfigId = previsionServiceConfigId(p)
    if (serviceConfigId == null) {return []}
    const waiting = p.ticketsWaiting ?? p.TicketsWaiting
    const entry: [number, ScidInfo] = [serviceConfigId, {
      sala: p.roomName ?? p.RoomName ?? p.serviceRoomName ?? p.ServiceRoomName ?? p.serviceRoom?.name ?? p.ServiceRoom?.name ?? p.room?.name ?? p.Room?.name ?? '',
      code: p.prefix ?? p.Prefix ?? p.serviceConfig?.prefix ?? p.serviceConfig?.Prefix ?? p.ServiceConfig?.prefix ?? p.ServiceConfig?.Prefix ?? '',
      nome: p.serviceConfigName ?? p.ServiceConfigName ?? p.serviceConfig?.name ?? p.serviceConfig?.Name ?? p.ServiceConfig?.name ?? p.ServiceConfig?.Name ?? p.name ?? p.Name ?? '',
      ticketsWaiting: typeof waiting === 'object' && waiting !== null
        ? Number(waiting.count ?? waiting.total ?? waiting.Total ?? waiting.waiting ?? 0)
        : Number(waiting) || 0,
    }]
    return [entry]
  }))
  return list
}

export function validPrevisions(list: ServiceConfigPrevisionDto[]): ValidServiceConfigPrevisionDto[] {
  return list
    .map((p) => {
      const serviceConfigId = previsionServiceConfigId(p)
      return serviceConfigId == null ? null : { ...p, serviceConfigId }
    })
    .filter((p): p is ServiceConfigPrevisionDto & { serviceConfigId: number } => p != null)
}

export async function ensurePrevisions(): Promise<void> {
  if (!_cache) {await loadPrevisions()}
}

export function invalidatePrevisions(): void { _cache = null }

export function scidInfo(serviceConfigId: number): ScidInfo | undefined {
  return _cache?.get(serviceConfigId)
}

/** Mapa serviceConfigId → ticketsWaiting (badge "N na fila"). Aproxima "elegíveis" no real. */
export function waitingByServiceConfig(): Record<number, number> {
  const out: Record<number, number> = {}
  _cache?.forEach((info, scid) => { out[scid] = info.ticketsWaiting ?? 0 })
  return out
}
